import { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  LayoutDashboard, 
  Image, 
  FileText, 
  Package, 
  HelpCircle,
  MessageSquare,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_spark-showcase-2/artifacts/k5fdni51_INTERCHAMPION%20C.I.E.%20CHAMPION%20OF%20POLAND%20GRAND%20CHAMPION%20OF%20POLAND%20CHAMPION%20OF%20SLOVAKIA%20CHAMPION%20OF%20HUNGARY%20CHAMPION%20OF%20CZECH%20REP.%20VIT%27AZ%20SLOVENSK%C3%81%20GRAND%20CHAMPION%20OF%20SLOVAKIA%20GRAND%20CHAMPION%20OF%20HUNGA%2824%29.png";

const getAuthHeaders = () => {
  const token = localStorage.getItem("adminToken");
  return { Authorization: `Bearer ${token}` };
};

// Sidebar Navigation
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: Image, label: "Realizacje", path: "/admin/portfolio" },
    { icon: FileText, label: "Blog", path: "/admin/blog" },
    { icon: Package, label: "Pakiety", path: "/admin/packages" },
    { icon: HelpCircle, label: "FAQ", path: "/admin/faq" },
    { icon: MessageSquare, label: "Wiadomości", path: "/admin/contacts" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
    toast.success("Wylogowano");
  };

  return (
    <aside className="w-64 bg-[#111] border-r border-white/5 min-h-screen p-6 flex flex-col">
      <Link to="/" className="mb-8">
        <img src={LOGO_URL} alt="ApisDesign" className="h-8" />
      </Link>
      
      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                isActive 
                  ? "bg-[#8B5CF6] text-white" 
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <button 
        onClick={handleLogout}
        className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors"
      >
        <LogOut className="w-5 h-5" />
        Wyloguj
      </button>
    </aside>
  );
};

// Dashboard
const Dashboard = () => {
  const [stats, setStats] = useState({ portfolio: 0, blog: 0, contacts: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [portfolio, blog, contacts] = await Promise.all([
          axios.get(`${API}/portfolio`),
          axios.get(`${API}/blog`),
          axios.get(`${API}/admin/contacts`, { headers: getAuthHeaders() })
        ]);
        setStats({
          portfolio: portfolio.data.length,
          blog: blog.data.length,
          contacts: contacts.data.filter(c => !c.read).length
        });
      } catch (error) {
        console.error(error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div>
      <h1 className="text-3xl font-display font-bold text-white mb-8">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#111] rounded-2xl p-6 border border-white/5">
          <Image className="w-8 h-8 text-[#8B5CF6] mb-4" />
          <p className="text-3xl font-bold text-white">{stats.portfolio}</p>
          <p className="text-white/50">Realizacji</p>
        </div>
        <div className="bg-[#111] rounded-2xl p-6 border border-white/5">
          <FileText className="w-8 h-8 text-[#F59E0B] mb-4" />
          <p className="text-3xl font-bold text-white">{stats.blog}</p>
          <p className="text-white/50">Wpisów na blogu</p>
        </div>
        <div className="bg-[#111] rounded-2xl p-6 border border-white/5">
          <MessageSquare className="w-8 h-8 text-green-500 mb-4" />
          <p className="text-3xl font-bold text-white">{stats.contacts}</p>
          <p className="text-white/50">Nowych wiadomości</p>
        </div>
      </div>
    </div>
  );
};

// Portfolio Management
const PortfolioAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    title: "", description: "", category: "web", image_url: "", link: "", tags: [], featured: false
  });

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setItems(response.data);
    } catch (error) {
      toast.error("Błąd ładowania");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await axios.put(`${API}/admin/portfolio/${editItem.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Zaktualizowano");
      } else {
        await axios.post(`${API}/admin/portfolio`, formData, { headers: getAuthHeaders() });
        toast.success("Dodano");
      }
      setDialogOpen(false);
      setEditItem(null);
      setFormData({ title: "", description: "", category: "web", image_url: "", link: "", tags: [], featured: false });
      fetchItems();
    } catch (error) {
      toast.error("Wystąpił błąd");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć?")) return;
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`, { headers: getAuthHeaders() });
      toast.success("Usunięto");
      fetchItems();
    } catch (error) {
      toast.error("Błąd usuwania");
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category,
      image_url: item.image_url,
      link: item.link || "",
      tags: item.tags || [],
      featured: item.featured
    });
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-display font-bold text-white">Realizacje</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#8B5CF6] hover:bg-[#7C3AED]" onClick={() => { setEditItem(null); setFormData({ title: "", description: "", category: "web", image_url: "", link: "", tags: [], featured: false }); }}>
              <Plus className="w-4 h-4 mr-2" /> Dodaj
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>{editItem ? "Edytuj" : "Dodaj"} realizację</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white/70">Tytuł</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Opis</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Kategoria</Label>
                <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2">
                  <option value="web">Strony WWW</option>
                  <option value="google">Google</option>
                  <option value="graphics">Grafiki</option>
                  <option value="apparel">Odzież</option>
                  <option value="branding">Branding</option>
                </select>
              </div>
              <div>
                <Label className="text-white/70">URL obrazu</Label>
                <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Link (opcjonalnie)</Label>
                <Input value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Tagi (oddzielone przecinkami)</Label>
                <Input value={formData.tags.join(", ")} onChange={(e) => setFormData({...formData, tags: e.target.value.split(",").map(t => t.trim())})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.featured} onCheckedChange={(checked) => setFormData({...formData, featured: checked})} />
                <Label className="text-white/70">Wyróżnione</Label>
              </div>
              <Button type="submit" className="w-full bg-[#8B5CF6]">Zapisz</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={item.id} className="bg-[#111] rounded-2xl overflow-hidden border border-white/5">
            <img src={item.image_url} alt={item.title} className="w-full h-40 object-cover" />
            <div className="p-4">
              <h3 className="font-bold text-white mb-1">{item.title}</h3>
              <p className="text-white/50 text-sm mb-3 line-clamp-2">{item.description}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(item)} className="border-white/10 text-white hover:bg-white/5">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(item.id)} className="border-red-500/30 text-red-500 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Blog Management
const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "", slug: "", excerpt: "", content: "", category: "web", image_url: "", published: true
  });

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, { headers: getAuthHeaders() });
      setPosts(response.data);
    } catch (error) {
      toast.error("Błąd ładowania");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editPost) {
        await axios.put(`${API}/admin/blog/${editPost.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Zaktualizowano");
      } else {
        await axios.post(`${API}/admin/blog`, formData, { headers: getAuthHeaders() });
        toast.success("Dodano");
      }
      setDialogOpen(false);
      setEditPost(null);
      setFormData({ title: "", slug: "", excerpt: "", content: "", category: "web", image_url: "", published: true });
      fetchPosts();
    } catch (error) {
      toast.error("Wystąpił błąd");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers: getAuthHeaders() });
      toast.success("Usunięto");
      fetchPosts();
    } catch (error) {
      toast.error("Błąd usuwania");
    }
  };

  const openEdit = (post) => {
    setEditPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image_url: post.image_url,
      published: post.published
    });
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-display font-bold text-white">Blog</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#8B5CF6] hover:bg-[#7C3AED]" onClick={() => { setEditPost(null); setFormData({ title: "", slug: "", excerpt: "", content: "", category: "web", image_url: "", published: true }); }}>
              <Plus className="w-4 h-4 mr-2" /> Dodaj wpis
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editPost ? "Edytuj" : "Dodaj"} wpis</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white/70">Tytuł</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Slug (URL)</Label>
                <Input value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Zajawka</Label>
                <Textarea value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} className="bg-white/5 border-white/10 text-white" rows={2} />
              </div>
              <div>
                <Label className="text-white/70">Treść (Markdown)</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="bg-white/5 border-white/10 text-white" rows={10} />
              </div>
              <div>
                <Label className="text-white/70">Kategoria</Label>
                <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2">
                  <option value="web">Strony WWW</option>
                  <option value="google">Google</option>
                  <option value="marketing">Marketing</option>
                  <option value="hodowla">Hodowla</option>
                </select>
              </div>
              <div>
                <Label className="text-white/70">URL obrazu</Label>
                <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.published} onCheckedChange={(checked) => setFormData({...formData, published: checked})} />
                <Label className="text-white/70">Opublikowany</Label>
              </div>
              <Button type="submit" className="w-full bg-[#8B5CF6]">Zapisz</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {posts.map((post) => (
          <div key={post.id} className="bg-[#111] rounded-2xl p-4 border border-white/5 flex items-center gap-4">
            <img src={post.image_url} alt={post.title} className="w-24 h-16 object-cover rounded-xl" />
            <div className="flex-1">
              <h3 className="font-bold text-white">{post.title}</h3>
              <p className="text-white/50 text-sm">{post.category} • {post.slug}</p>
            </div>
            <div className="flex items-center gap-2">
              {post.published ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-white/30" />}
              <Button size="sm" variant="outline" onClick={() => openEdit(post)} className="border-white/10 text-white hover:bg-white/5">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleDelete(post.id)} className="border-red-500/30 text-red-500 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Packages Management (simplified)
const PackagesAdmin = () => {
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    axios.get(`${API}/packages`).then(res => setPackages(res.data));
  }, []);

  return (
    <div>
      <h1 className="text-3xl font-display font-bold text-white mb-8">Pakiety</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {packages.map((pkg) => (
          <div key={pkg.id} className="bg-[#111] rounded-2xl p-6 border border-white/5">
            <h3 className="font-bold text-white text-xl mb-2">{pkg.name}</h3>
            <p className="text-white/50 text-sm mb-4">{pkg.description}</p>
            <p className="text-[#8B5CF6] font-bold">{pkg.price}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// FAQ Management (simplified)
const FAQAdmin = () => {
  const [faq, setFaq] = useState([]);
  useEffect(() => {
    axios.get(`${API}/faq`).then(res => setFaq(res.data));
  }, []);

  return (
    <div>
      <h1 className="text-3xl font-display font-bold text-white mb-8">FAQ</h1>
      <div className="space-y-4">
        {faq.map((item) => (
          <div key={item.id} className="bg-[#111] rounded-2xl p-6 border border-white/5">
            <h3 className="font-bold text-white mb-2">{item.question}</h3>
            <p className="text-white/50 text-sm">{item.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Contacts Management
const ContactsAdmin = () => {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, { headers: getAuthHeaders() })
      .then(res => setContacts(res.data))
      .catch(() => toast.error("Błąd ładowania"));
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/read`, {}, { headers: getAuthHeaders() });
      setContacts(contacts.map(c => c.id === id ? { ...c, read: true } : c));
    } catch (error) {
      toast.error("Błąd");
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-display font-bold text-white mb-8">Wiadomości</h1>
      <div className="space-y-4">
        {contacts.map((contact) => (
          <div key={contact.id} className={`bg-[#111] rounded-2xl p-6 border ${contact.read ? "border-white/5" : "border-[#8B5CF6]/50"}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-white">{contact.name}</h3>
                <p className="text-white/50 text-sm">{contact.email} {contact.phone && `• ${contact.phone}`}</p>
              </div>
              {!contact.read && (
                <Button size="sm" onClick={() => markAsRead(contact.id)} className="bg-[#8B5CF6]/20 text-[#8B5CF6] hover:bg-[#8B5CF6]/30">
                  Oznacz jako przeczytane
                </Button>
              )}
            </div>
            {contact.services?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {contact.services.map((s, i) => (
                  <span key={i} className="px-2 py-1 bg-white/5 rounded text-xs text-white/60">{s}</span>
                ))}
              </div>
            )}
            <p className="text-white/70">{contact.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Admin Component
const Admin = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate]);

  return (
    <div className="flex min-h-screen bg-[#0a0a0a]" data-testid="admin-page">
      <Sidebar />
      <main className="flex-1 p-8">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="portfolio" element={<PortfolioAdmin />} />
          <Route path="blog" element={<BlogAdmin />} />
          <Route path="packages" element={<PackagesAdmin />} />
          <Route path="faq" element={<FAQAdmin />} />
          <Route path="contacts" element={<ContactsAdmin />} />
        </Routes>
      </main>
    </div>
  );
};

export default Admin;
